package dev.alimmz.portfolio.presentation.screen

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.alimmz.portfolio.domain.model.Project
import dev.alimmz.portfolio.presentation.theme.AndroidGreen

/** Responsive project presentation using the portfolio's existing content and theme. */
@Composable
fun ProjectDetailScreen(project: Project, onBack: () -> Unit) {
    val uriHandler = LocalUriHandler.current
    // A newly selected project starts at the top, even when this screen is reused.
    key(project.id) {
        BoxWithConstraints(
            modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background),
            contentAlignment = Alignment.TopCenter
        ) {
            val isWide = maxWidth >= 900.dp
            val gutter = if (maxWidth < 600.dp) 20.dp else 40.dp
            Column(modifier = Modifier.widthIn(max = 1280.dp).fillMaxSize()) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = gutter, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    TextButton(onClick = onBack) {
                        DetailArrow(color = MaterialTheme.colorScheme.onBackground, back = true)
                        Spacer(Modifier.width(10.dp))
                        Text("All projects", color = MaterialTheme.colorScheme.onBackground)
                    }
                    Text(
                        "ALIMMZ.DEV",
                        style = MaterialTheme.typography.labelSmall,
                        letterSpacing = 1.5.sp,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.35f))
                LazyColumn(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    contentPadding = PaddingValues(
                        start = gutter, end = gutter,
                        top = if (isWide) 56.dp else 32.dp, bottom = 48.dp
                    ),
                    verticalArrangement = Arrangement.spacedBy(if (isWide) 48.dp else 32.dp)
                ) {
                    item {
                        if (isWide) {
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(48.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                ProjectHeading(project, true, Modifier.weight(1.1f)) {
                                    uriHandler.openUri(project.link)
                                }
                                ProjectArtwork(project, Modifier.weight(0.9f).height(380.dp))
                            }
                        } else {
                            Column(verticalArrangement = Arrangement.spacedBy(28.dp)) {
                                ProjectHeading(project, false, Modifier.fillMaxWidth()) {
                                    uriHandler.openUri(project.link)
                                }
                                ProjectArtwork(project, Modifier.fillMaxWidth().height(300.dp))
                            }
                        }
                    }
                    item {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.45f))
                    }
                    item {
                        if (isWide) {
                            Row(horizontalArrangement = Arrangement.spacedBy(40.dp)) {
                                ProjectOverview(project, Modifier.weight(1.1f))
                                ProjectStack(project, Modifier.weight(0.9f))
                            }
                        } else {
                            Column(verticalArrangement = Arrangement.spacedBy(28.dp)) {
                                ProjectOverview(project, Modifier.fillMaxWidth())
                                ProjectStack(project, Modifier.fillMaxWidth())
                            }
                        }
                    }
                    item { ProjectReadme(project) }
                    item {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.35f))
                        TextButton(onClick = onBack, modifier = Modifier.padding(top = 20.dp)) {
                            DetailArrow(color = MaterialTheme.colorScheme.onBackground, back = true)
                            Spacer(Modifier.width(10.dp))
                            Text("Explore more projects", color = MaterialTheme.colorScheme.onBackground)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProjectHeading(
    project: Project,
    isWide: Boolean,
    modifier: Modifier,
    onOpenLink: () -> Unit
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(24.dp)) {
        Surface(
            color = MaterialTheme.colorScheme.primaryContainer,
            shape = RoundedCornerShape(50)
        ) {
            Text(
                "SELECTED WORK / ${project.id.padStart(2, '0')}",
                modifier = Modifier.padding(horizontal = 14.dp, vertical = 9.dp),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onPrimaryContainer,
                fontWeight = FontWeight.Bold,
                letterSpacing = 1.5.sp
            )
        }
        Text(
            project.title,
            modifier = Modifier.semantics { heading() },
            fontSize = if (isWide) 64.sp else 42.sp,
            lineHeight = if (isWide) 72.sp else 50.sp,
            fontWeight = FontWeight.Black,
            letterSpacing = (-1.5).sp,
            color = MaterialTheme.colorScheme.onBackground
        )
        Text(
            project.description,
            style = MaterialTheme.typography.titleLarge,
            lineHeight = 32.sp,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        if (project.link.isNotBlank()) {
            Button(
                onClick = onOpenLink,
                shape = RoundedCornerShape(14.dp),
                contentPadding = PaddingValues(horizontal = 22.dp, vertical = 16.dp)
            ) {
                Text("View project", fontWeight = FontWeight.Bold)
                Spacer(Modifier.width(12.dp))
                DetailArrow(color = MaterialTheme.colorScheme.onPrimary)
            }
        }
    }
}

/** Abstract cover artwork: project identity, rather than a simulated app screenshot. */
@Composable
private fun ProjectIdentity(project: Project, modifier: Modifier) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(28.dp))
            .background(Brush.linearGradient(listOf(Color(0xFF183F32), Color(0xFF0B211B))))
    ) {
        Canvas(Modifier.matchParentSize()) {
            val center = Offset(size.width * 0.78f, size.height * 0.35f)
            repeat(4) { index ->
                drawCircle(
                    color = AndroidGreen.copy(alpha = 0.10f),
                    radius = size.minDimension * (0.24f + index * 0.19f),
                    center = center,
                    style = Stroke(width = 1.dp.toPx())
                )
            }
            drawCircle(
                color = AndroidGreen.copy(alpha = 0.08f),
                radius = size.minDimension * 0.3f,
                center = Offset(size.width * 0.1f, size.height * 0.95f)
            )
        }
        Column(
            modifier = Modifier.fillMaxWidth().padding(28.dp),
            verticalArrangement = Arrangement.spacedBy(28.dp)
        ) {
            Text(
                "PROJECT ${project.id.padStart(2, '0')}",
                style = MaterialTheme.typography.labelSmall,
                letterSpacing = 2.sp,
                color = AndroidGreen,
                fontWeight = FontWeight.Bold
            )
            Surface(
                shape = RoundedCornerShape(24.dp),
                color = AndroidGreen,
                modifier = Modifier.size(104.dp)
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(
                        project.title.take(1).uppercase(),
                        fontSize = 60.sp,
                        fontWeight = FontWeight.Black,
                        color = Color(0xFF0B211B)
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    project.title,
                    style = MaterialTheme.typography.headlineMedium,
                    color = Color.White,
                    fontWeight = FontWeight.Bold
                )
                project.technologies.firstOrNull()?.let { technology ->
                    Text(
                        technology,
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color(0xFFBDD8CA)
                    )
                }
            }
        }
    }
}

@Composable
private fun ProjectOverview(project: Project, modifier: Modifier) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(18.dp)) {
        DetailEyebrow("01 / ABOUT THE PROJECT")
        Text(
            "Project overview",
            modifier = Modifier.semantics { heading() },
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onBackground
        )
        Text(
            project.description,
            style = MaterialTheme.typography.bodyLarge,
            lineHeight = 28.sp,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ProjectStack(project: Project, modifier: Modifier) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.45f))
    ) {
        Column(
            modifier = Modifier.padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp)
        ) {
            DetailEyebrow("02 / UNDER THE HOOD")
            Text(
                "Technology stack",
                modifier = Modifier.semantics { heading() },
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            if (project.technologies.isNotEmpty()) {
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    project.technologies.forEach { technology ->
                        Surface(
                            color = MaterialTheme.colorScheme.surfaceVariant,
                            shape = RoundedCornerShape(10.dp)
                        ) {
                            Text(
                                technology,
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 10.dp),
                                style = MaterialTheme.typography.labelLarge,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            } else {
                Text(
                    "Technology details coming soon.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
private fun DetailEyebrow(text: String) {
    Text(
        text,
        style = MaterialTheme.typography.labelSmall,
        letterSpacing = 1.5.sp,
        fontWeight = FontWeight.Bold,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
}

@Composable
private fun DetailArrow(color: Color, back: Boolean = false) {
    Canvas(Modifier.size(18.dp)) {
        val stroke = 1.8.dp.toPx()
        if (back) {
            drawLine(color, Offset(size.width * 0.85f, size.height * 0.5f), Offset(size.width * 0.15f, size.height * 0.5f), stroke, StrokeCap.Round)
            drawLine(color, Offset(size.width * 0.15f, size.height * 0.5f), Offset(size.width * 0.45f, size.height * 0.2f), stroke, StrokeCap.Round)
            drawLine(color, Offset(size.width * 0.15f, size.height * 0.5f), Offset(size.width * 0.45f, size.height * 0.8f), stroke, StrokeCap.Round)
        } else {
            drawLine(color, Offset(size.width * 0.2f, size.height * 0.8f), Offset(size.width * 0.8f, size.height * 0.2f), stroke, StrokeCap.Round)
            drawLine(color, Offset(size.width * 0.3f, size.height * 0.2f), Offset(size.width * 0.8f, size.height * 0.2f), stroke, StrokeCap.Round)
            drawLine(color, Offset(size.width * 0.8f, size.height * 0.2f), Offset(size.width * 0.8f, size.height * 0.7f), stroke, StrokeCap.Round)
        }
    }
}

